import * as React from 'react'
import { Button, Drawer, executeCleanDrawerRender, Well } from '@pushly/aqe/lib/components'
import { CheckCircleOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { notification, Select, Skeleton, Switch, Tag, Tooltip } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import CopyToClipboard from 'react-copy-to-clipboard'
import { simpleFormErrorNotification, simpleNotification } from '@halo/_utils/notifications'
import { FormComponentProps } from '@ant-design/compatible/lib/form'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { legacyValidateFields } from '@halo/_utils/antd'
import { AppState } from '@halo/stores/app.state'
import cloneDeep from 'clone-deep'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import { DomainDto } from '@halo/dtos/domain.dto'
import { DomainService } from '@halo/services/domain.service'
import * as randomstring from 'randomstring'
import { PlatformFlagEntities } from '@halo/enums/platform-flag-entities'
import { FlagModel } from '@halo/models/flag.model'
import { arraySortByModelGetter } from '@halo/_utils/array'
import FlagList from '@halo/structs/flag-list'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asCaslSubject } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'

interface IDomainServerEditor extends FormComponentProps {
    domain: DomainDto
    flags: FlagList
    visible: boolean
    onClose: (e: any) => void
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDSEState {
    rawFlagsVisible: boolean
}

export const DomainServerEditor = Form.create<IDomainServerEditor>()(
    class extends React.Component<IDomainServerEditor, IDSEState> {
        public state: IDSEState = {
            rawFlagsVisible: false,
        }

        protected appState: AppState
        protected appService: AppService
        protected domainService: DomainService
        protected visibleFlagRefs: any = {}
        protected devFlagRefs: any = {}

        public constructor(props: IDomainServerEditor) {
            super(props)

            this.appState = Container.get(AppState)
            this.appService = Container.get(AppService)
            this.domainService = Container.get(DomainService)

            executeCleanDrawerRender(this)
        }

        public render() {
            const currentUserIsDev = this.appState.currentUser?.isInternalDevUser || false
            const currentValues = this.getCurrentValues()

            const allActiveFlags = this.props.flags.getActiveFlags()
            const exposedFlags: FlagModel[] = arraySortByModelGetter(
                allActiveFlags.filter((flag: FlagModel) => !flag.getIsDevOnly()),
                'getDisplayName',
            )
            const devOnlyFlags: FlagModel[] = arraySortByModelGetter(
                allActiveFlags.filter((flag: FlagModel) => flag.getIsDevOnly() && flag.getIsActive()),
                'getDisplayName',
            )

            return (
                <Drawer
                    className={classnames('ddf-server-editor')}
                    title="Update Server Keys & Flags"
                    placement="right"
                    closable={true}
                    onSubmit={this.handleSubmit}
                    disableSubmit={Object.keys(currentValues).length === 0}
                    onClose={this.props.onClose}
                    visible={this.props.visible}
                >
                    <Form {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}>
                        <Well title="Notification Keys" mode="ghost" hideFooter={true}>
                            <Form.Item label="Collapse Key" className={classnames('ddf-server-editor-ck')}>
                                {this.props.form.getFieldDecorator('collapseKeyType', {
                                    initialValue: this.domain.collapse_key_type,
                                })(
                                    <Select size="small">
                                        <Select.Option value="DOMAIN_ID">Domain Level</Select.Option>
                                        <Select.Option value="NOTIFICATION_SCHEDULE_ID">
                                            Notification Level
                                        </Select.Option>
                                    </Select>,
                                )}
                            </Form.Item>
                        </Well>

                        <Well title="Flags" mode="ghost" hideFooter={true}>
                            <Form.Item label="Service & Feature Flags">
                                <div className={classnames(null, 'flag-toggles')}>
                                    {exposedFlags.map((flag, idx) => {
                                        const flagsPerRow = 2
                                        const shouldBreak = idx >= flagsPerRow && idx % flagsPerRow === 0

                                        return (
                                            <React.Fragment key={idx}>
                                                {shouldBreak && <div className="break" />}
                                                <label className="flag-toggle">
                                                    <Switch
                                                        ref={(el) => (this.visibleFlagRefs[flag.getKey()] = el)}
                                                        size="small"
                                                        defaultChecked={this.domain.flags?.includes(flag.getKey())}
                                                        onChange={(enabled) => this.updateRawFlags(flag, enabled)}
                                                    />
                                                    <Tooltip title={flag.getDescription() || flag.getDisplayName()}>
                                                        <span>{flag.getDisplayName() ?? flag.getKey()}</span>
                                                    </Tooltip>
                                                </label>
                                            </React.Fragment>
                                        )
                                    })}
                                </div>
                            </Form.Item>
                        </Well>

                        <Well
                            className={classnames(null, {
                                'hide-visibility': !currentUserIsDev,
                            })}
                            title="Additional Flags (Dev Access Only)"
                            mode="ghost"
                            hideFooter={true}
                        >
                            <Form.Item label="Feature Flags">
                                <div className={classnames(null, 'flag-toggles')}>
                                    {devOnlyFlags.map((flag, idx) => {
                                        const flagsPerRow = 2
                                        const shouldBreak = idx >= flagsPerRow && idx % flagsPerRow === 0

                                        return (
                                            <React.Fragment key={idx}>
                                                {shouldBreak && <div className="break" />}
                                                <label className="flag-toggle">
                                                    <Switch
                                                        ref={(el) => (this.devFlagRefs[flag.getKey()] = el)}
                                                        size="small"
                                                        defaultChecked={this.domain.flags?.includes(flag.getKey())}
                                                        onChange={(enabled) => this.updateRawFlags(flag, enabled)}
                                                    />
                                                    <Tooltip title={flag.getDescription() || flag.getDisplayName()}>
                                                        <span>{flag.getDisplayName() ?? flag.getKey()}</span>
                                                    </Tooltip>
                                                </label>
                                            </React.Fragment>
                                        )
                                    })}
                                </div>
                            </Form.Item>

                            <Form.Item
                                className={classnames('ddf-service-editor-flags', {
                                    visible: this.state.rawFlagsVisible,
                                })}
                                label={
                                    <div className="switch-row">
                                        <div className="switch-left">Raw Flag Editor</div>
                                        <div className="switch-right">
                                            <Switch
                                                size="small"
                                                checked={this.state.rawFlagsVisible}
                                                onChange={(v) => this.setState({ rawFlagsVisible: v })}
                                            />
                                        </div>
                                    </div>
                                }
                            >
                                {this.props.form.getFieldDecorator('flags', {
                                    initialValue: [...(this.domain.flags || [])],
                                })(
                                    <Select<string[]>
                                        size="small"
                                        mode="tags"
                                        showSearch={false}
                                        tokenSeparators={[',', ';', ' ', '\n']}
                                        onChange={this.updateFlagToggles}
                                    />,
                                )}
                            </Form.Item>
                        </Well>
                    </Form>
                </Drawer>
            )
        }

        protected get domain(): DomainDto {
            return this.props.domain
        }

        protected updateFlagToggles = (flags: string[]): void => {
            Object.keys(this.visibleFlagRefs).forEach((flag) => {
                const ref = this.visibleFlagRefs[flag]

                if (ref?.rcSwitch) {
                    ref.rcSwitch.setState({ checked: flags.includes(flag) })
                }
            })

            Object.keys(this.devFlagRefs).forEach((flag) => {
                const ref = this.devFlagRefs[flag]

                if (ref?.rcSwitch) {
                    ref.rcSwitch.setState({ checked: flags.includes(flag) })
                }
            })
        }

        protected updateRawFlags(flag: FlagModel, enabled: boolean): void {
            const formFlags: string[] = this.props.form.getFieldValue('flags') ?? []
            if (enabled && !formFlags.includes(flag.getKey())) {
                formFlags.push(flag.getKey())
            } else if (!enabled && formFlags.includes(flag.getKey())) {
                formFlags.splice(
                    formFlags.findIndex((f) => f === flag.getKey()),
                    1,
                )
            }

            this.props.form.setFieldsValue({ flags: formFlags })
            this.updateFlagToggles(formFlags)

            this.forceUpdate()
        }

        protected getCurrentValues(): Partial<DomainDto> {
            let values: Partial<DomainDto> = {}
            const currentUserIsDev = this.appState.currentUser?.isInternalDevUser || false

            values.flags = this.props.form.getFieldValue('flags') || []
            values.flags = values.flags.map((flag) => flag.toUpperCase())
            const valueFlags = cloneDeep(values.flags).sort().join(',')
            const domainFlags = cloneDeep(this.domain.flags || [])
                .sort()
                .join(',')
            if (valueFlags === domainFlags) {
                delete values.flags
            }

            values.collapse_key_type = this.props.form.getFieldValue('collapseKeyType')
            if (values.collapse_key_type === this.domain.collapse_key_type) {
                delete values.collapse_key_type
            }

            return values
        }

        protected handleSubmit = async () => {
            try {
                await legacyValidateFields(this.props.form)
                const values = this.getCurrentValues()

                let { ok, data } = await this.domainService.patch(this.domain.id, values, {
                    showLoadingScreen: true,
                })

                if (ok) {
                    this.props.onDomainUpdated(data)
                    this.props.onClose(null)
                }
            } catch (err) {
                simpleFormErrorNotification(err)
            }
        }
    },
)

interface IDomainServerWell {
    loading?: boolean
    domain: DomainDto
    title?: string
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDSWState {
    showDrawer: boolean
    drawerKey?: string
    flags?: FlagList
}

export class DomainServerWell extends React.Component<IDomainServerWell, IDSWState> {
    public state: IDSWState = {
        showDrawer: false,
        flags: FlagList.from([]),
    }

    protected appState: AppState
    protected appService: AppService

    public constructor(props: IDomainServerWell) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    public componentDidMount() {
        this.getActiveFlags()
    }

    public render() {
        return (
            <Well
                className="type-3 nested server-well"
                title="Server Keys & Flags"
                actions={
                    this.appState.abilityStore.can(
                        AbilityAction.UPDATE,
                        asCaslSubject(SubjectEntity.DOMAIN, {
                            ...this.props.domain,
                            statusId: this.props.domain?.status_id,
                        }),
                    ) && (
                        <Button key="edit" size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                            <EditOutlined />
                            <span>Edit</span>
                        </Button>
                    )
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            {this.renderNotificationWell()}
                            {this.renderFlagsWell()}

                            <DomainServerEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                domain={this.props.domain}
                                flags={this.state.flags}
                                onClose={this.handleDrawerClose}
                                onDomainUpdated={this.props.onDomainUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderNotificationWell() {
        const currentUserIsDev = this.appState.currentUser?.isInternalDevUser || false

        return (
            <Well title="Notification Keys" mode="ghost" hideFooter={true}>
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Collapse Key</div>
                    <div className={classnames('form-layout-row-value', 'can-copy')}>
                        <CopyToClipboard
                            onCopy={() => this.handleCopied('collapse key')}
                            text={this.domain.collapse_key_type}
                        >
                            <span>
                                {this.domain.collapse_key_type.toUpperCase() === 'DOMAIN_ID'
                                    ? 'Domain Level'
                                    : 'Notification Level'}
                            </span>
                        </CopyToClipboard>
                    </div>
                </div>
            </Well>
        )
    }

    protected renderFlagsWell() {
        const actingUserIsDevUser = this.appState.currentUser?.isInternalDevUser ?? false

        const allFlags = this.state.flags
        const exposedFlags = allFlags.getActiveFlags().filter((flag: FlagModel) => !flag.getIsDevOnly())
        const sortedFlags = arraySortByModelGetter(exposedFlags, 'getDisplayName')

        return (
            <Well title="Flags" mode="ghost" hideFooter={true}>
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Service & Feature Flags</div>
                    <div className={classnames('form-layout-row-value flag-toggles')}>
                        {sortedFlags
                            .filter((f) => this.domain.flags?.includes(f.getKey()))
                            .map((flag, idx) => {
                                const flagsPerRow = 3
                                const shouldBreak = idx >= flagsPerRow && idx % flagsPerRow === 0

                                return (
                                    <React.Fragment key={idx}>
                                        {shouldBreak && <div className="break" />}
                                        <Tooltip title={flag.getDescription()}>
                                            <span className="flag-toggle">
                                                <CheckCircleOutlined className="flag-enabled" />
                                                <span>{flag.getDisplayName() ?? flag.getKey()}</span>
                                            </span>
                                        </Tooltip>
                                    </React.Fragment>
                                )
                            })}
                    </div>
                </div>

                {actingUserIsDevUser && (
                    <>
                        <br />
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>
                                All Flags (Dev Access Only)
                                <div className={classnames('form-layout-row-sub')}>
                                    <InfoCircleOutlined />
                                    <span>
                                        View{' '}
                                        <a
                                            href="https://pushly.atlassian.net/wiki/spaces/AQ/pages/372080658/Flags#Domain-Flags"
                                            target="_blank"
                                        >
                                            Available Tags
                                        </a>
                                    </span>
                                </div>
                            </div>
                            <div className={classnames('form-layout-row-value')}>
                                {this.domain.flags?.map((flagKey, idx) => (
                                    <Tag
                                        key={idx}
                                        className={classnames('entity-flag-tag', {
                                            unknown: !allFlags.findByKey(flagKey),
                                            deprecated: allFlags.findByKey(
                                                flagKey,
                                                (flag: FlagModel) => !flag.getIsActive(),
                                            ),
                                        })}
                                    >
                                        {flagKey}
                                    </Tag>
                                )) || 'No flags'}
                            </div>
                        </div>
                    </>
                )}
            </Well>
        )
    }

    protected get domain(): DomainDto {
        return this.props.domain
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected getActiveFlags = async () => {
        const query = {
            entities: [PlatformFlagEntities.DOMAIN, PlatformFlagEntities.DEMO_DOMAIN].join(','),
            pagination: '0',
        }

        let { data: flags } = await this.appService.fetchFlags({ query })
        flags = FlagList.from(flags.map((f: any) => FlagModel.build(f)))

        return this.setState({ flags })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
